import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Phone, Mail, Instagram, MessageCircle, Pencil, Trash2, ArrowLeft, Send, Trophy, Clock, MoveHorizontal, CalendarDays, Camera, Loader2, Home, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, LEVELS, SIDES, EMOJIS, waLink, igLink } from "../lib/api";
import { Avatar, Field, LevelBadge, Loader, Empty } from "../components/common";
import { ImageUpload } from "../components/ImageUpload";
import { ProfilePhotos } from "../components/ProfilePhotos";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const blank = { name: "", email: "", phone: "", instagram: "", level: "", court_side: "", play_time: "", badge_emoji: "", bio: "", photo: null, ct_name: "", training: null };

export const AthleteForm = ({ open, onOpenChange, athlete, onSaved }) => {
  const { user, isAdmin } = useAuth();
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(athlete ? { ...blank, ...athlete } : blank); }, [open, athlete]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = Object.fromEntries(Object.keys(blank).map((k) => [k, f[k]]));
    if (isAdmin && athlete && athlete.user_id !== user.user_id) body.role = f.role;
    try {
      const { data } = athlete ? await api.put(`/athletes/${athlete.user_id}`, body) : await api.post("/athletes", body);
      toast.success("Perfil salvo");
      onSaved(data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-lg">
        <DialogHeader><DialogTitle className="h-display text-3xl">{athlete ? "Editar perfil" : "Novo atleta"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Foto"><ImageUpload round value={f.photo} onChange={set("photo")} testid="athlete-photo-upload" label="Foto" /></Field>
          <Field label="Nome"><input required data-testid="athlete-name-input" className="inp" value={f.name} onChange={set("name")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone / WhatsApp"><input data-testid="athlete-phone-input" className="inp" value={f.phone} onChange={set("phone")} placeholder="(48) 99999-9999" /></Field>
            <Field label="Instagram"><input data-testid="athlete-instagram-input" className="inp" value={f.instagram} onChange={set("instagram")} placeholder="@usuario" /></Field>
          </div>
          {isAdmin && <Field label="E-mail"><input data-testid="athlete-email-input" type="email" className="inp" value={f.email} onChange={set("email")} disabled={athlete && !athlete.manual} /></Field>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nível">
              <select data-testid="athlete-level-select" className="inp" value={f.level} onChange={set("level")}><option value="">Selecione</option>{LEVELS.map((l) => <option key={l}>{l}</option>)}</select>
            </Field>
            <Field label="Lado da quadra">
              <select data-testid="athlete-side-select" className="inp" value={f.court_side} onChange={set("court_side")}><option value="">Selecione</option>{SIDES.map((l) => <option key={l}>{l}</option>)}</select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome do CT"><input data-testid="athlete-ct-input" className="inp" value={f.ct_name || ""} onChange={set("ct_name")} /></Field>
            <Field label="Está treinando?">
              <select data-testid="athlete-training-select" className="inp" value={f.training === true ? "1" : f.training === false ? "0" : ""} onChange={(e) => setF({ ...f, training: e.target.value === "" ? null : e.target.value === "1" })}>
                <option value="">—</option><option value="1">Sim</option><option value="0">Não</option>
              </select>
            </Field>
          </div>
          <Field label="Há quanto tempo joga futevôlei"><input data-testid="athlete-playtime-input" className="inp" value={f.play_time} onChange={set("play_time")} placeholder="Ex: 3 anos" /></Field>
          {isAdmin && (
            <Field label="Selo (emoji ao lado da foto)">
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setF({ ...f, badge_emoji: "" })} className={`h-9 rounded-lg px-2 text-xs ${!f.badge_emoji ? "bg-[#E4F222] text-black" : "border border-[#232B3E] text-[#94A3B8]"}`}>Nenhum</button>
                {EMOJIS.map((em) => (
                  <button type="button" key={em} data-testid={`emoji-${em}`} onClick={() => setF({ ...f, badge_emoji: em })} className={`h-9 w-9 rounded-lg text-lg ${f.badge_emoji === em ? "bg-[#E4F222]" : "border border-[#232B3E]"}`}>{em}</button>
                ))}
                <input className="inp h-9 w-20" maxLength={4} placeholder="Outro" value={EMOJIS.includes(f.badge_emoji) ? "" : f.badge_emoji} onChange={set("badge_emoji")} />
              </div>
            </Field>
          )}
          {isAdmin && athlete && athlete.user_id !== user.user_id && !athlete.manual && (
            <Field label="Permissão">
              <select data-testid="athlete-role-select" className="inp" value={f.role || "athlete"} onChange={set("role")}><option value="athlete">Atleta</option><option value="admin">Administrador</option></select>
            </Field>
          )}
          <Field label="Sobre"><textarea data-testid="athlete-bio-input" rows={3} className="inp" value={f.bio} onChange={set("bio")} /></Field>
          <button disabled={busy} data-testid="athlete-save-btn" className="btn-lime w-full rounded-full py-3">{busy ? "Salvando..." : "Salvar"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Comments = ({ uid }) => {
  const { user, isAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get(`/athletes/${uid}/comments`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/athletes/${uid}/comments`, { text }); setText(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { await api.delete(`/comments/${id}`); load(); };
  return (
    <div className="space-y-4">
      <form onSubmit={send} className="flex gap-2">
        <input data-testid="comment-input" className="inp" placeholder="Deixe um comentário..." value={text} onChange={(e) => setText(e.target.value)} />
        <button data-testid="comment-send-btn" className="btn-lime rounded-xl px-4"><Send className="h-4 w-4" /></button>
      </form>
      {list.length === 0 ? <p className="text-sm text-[#64748B]">Nenhum comentário ainda. Seja o primeiro!</p> : list.map((c) => (
        <div key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3">
          <Avatar user={{ name: c.author_name, google_picture: c.author_photo?.startsWith("http") ? c.author_photo : null, photo: c.author_photo?.startsWith("http") ? null : c.author_photo, badge_emoji: c.author_emoji }} size={34} />
          <div className="card-x flex-1 p-3">
            <div className="flex items-center justify-between text-xs"><b>{c.author_name}</b>
              <span className="flex items-center gap-2 text-[#64748B]">{new Date(c.created_at).toLocaleDateString("pt-BR")}
                {(isAdmin || c.author_id === user.user_id || uid === user.user_id) && <button data-testid={`delete-comment-${c.id}`} onClick={() => del(c.id)} className="hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
              </span>
            </div>
            <p className="mt-1 text-sm text-[#cbd5e1]">{c.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const Row = ({ icon: I, label, value }) => (
  <div className="flex items-center justify-between border-b border-[#232B3E] py-3 text-sm last:border-0">
    <span className="flex items-center gap-2 text-[#94A3B8]"><I className="h-4 w-4" />{label}</span><span className="text-right font-medium">{value || "—"}</span>
  </div>
);

const MainPhoto = ({ a, canEdit, onSaved }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      await api.put(`/athletes/${a.user_id}`, { photo: data.path });
      toast.success("Foto principal atualizada");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="relative">
      <Avatar user={a} size={96} ring />
      {canEdit && (
        <>
          <button data-testid="main-photo-btn" title="Alterar foto principal" onClick={() => ref.current?.click()}
            className="absolute -left-1 -top-1 flex h-9 w-9 items-center justify-center rounded-full bg-[#E4F222] text-black shadow-lg">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          </button>
          <input ref={ref} type="file" accept="image/*" className="hidden" data-testid="main-photo-input" onChange={pick} />
        </>
      )}
    </div>
  );
};

export default function AthleteProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, refresh } = useAuth();
  const [a, setA] = useState(null);
  const [tab, setTab] = useState("dados");
  const [edit, setEdit] = useState(false);
  const load = () => api.get(`/athletes/${id}`).then((r) => setA(r.data)).catch(() => setA(false));
  useEffect(() => { setA(null); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (a === null) return <Loader />;
  if (a === false) return <Empty title="Atleta não encontrado" />;
  const me = a.user_id === user.user_id;
  const wa = waLink(a.phone, `Olá ${a.name.split(" ")[0]}! Te vi no app do Circuito Altax.`);
  const remove = async () => {
    if (!window.confirm(`Excluir ${a.name}? Essa ação não pode ser desfeita.`)) return;
    try { await api.delete(`/athletes/${a.user_id}`); toast.success("Atleta excluído"); navigate("/app/atletas"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-3xl">
      <button onClick={() => navigate(-1)} data-testid="profile-back-btn" className="mb-4 flex items-center gap-1 text-sm text-[#94A3B8] hover:text-white"><ArrowLeft className="h-4 w-4" />Voltar</button>
      <div className="card-x relative overflow-hidden p-6 rise">
        <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[#E4F222]/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-5">
          <MainPhoto a={a} canEdit={me || isAdmin} onSaved={() => { load(); if (me) refresh(); }} />
          <div className="min-w-0 flex-1">
            <h1 data-testid="profile-name" className="h-display text-4xl">{a.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[#94A3B8]"><LevelBadge level={a.level} />{a.court_side && <span>Lado {a.court_side.toLowerCase()}</span>}</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {wa && <a data-testid="profile-whatsapp-btn" href={wa} target="_blank" rel="noreferrer" className="btn-lime flex items-center gap-1.5 rounded-full px-4 py-2 text-sm"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
              {a.phone && <a data-testid="profile-call-btn" href={`tel:${a.phone}`} className="btn-ghost"><Phone className="h-4 w-4" /></a>}
              {a.instagram && <a data-testid="profile-instagram-btn" href={igLink(a.instagram)} target="_blank" rel="noreferrer" className="btn-ghost"><Instagram className="h-4 w-4" /></a>}
              {(me || isAdmin) && <button data-testid="profile-edit-btn" onClick={() => setEdit(true)} className="btn-ghost flex items-center gap-1"><Pencil className="h-4 w-4" />Editar</button>}
              {isAdmin && !me && <button data-testid="profile-delete-btn" onClick={remove} className="btn-ghost text-red-400"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
        </div>
        <div className="relative mt-6 grid grid-cols-3 divide-x divide-[#232B3E] text-center">
          <div><div data-testid="profile-total-points" className="font-display text-3xl font-bold text-[#E4F222]">{a.total_points}</div><div className="text-xs text-[#94A3B8]">Pontos</div></div>
          <div><div className="font-display text-3xl font-bold">{a.titles}</div><div className="text-xs text-[#94A3B8]">Títulos</div></div>
          <div><div className="font-display text-3xl font-bold">{a.registrations_count}</div><div className="text-xs text-[#94A3B8]">Inscrições</div></div>
        </div>
      </div>

      <ProfilePhotos key={a.user_id} athlete={a} />

      <div className="my-5 flex gap-2">
        {[["dados", "Dados"], ["pontos", "Pontuação"], ["comentarios", "Comentários"]].map(([v, l]) => (
          <button key={v} data-testid={`profile-tab-${v}`} onClick={() => setTab(v)} className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === v ? "bg-[#E4F222] text-black" : "text-[#94A3B8]"}`}>{l}</button>
        ))}
      </div>
      {tab === "dados" && (
        <div className="card-x px-5 py-2">
          <Row icon={Phone} label="Telefone" value={a.phone} />
          <Row icon={Mail} label="E-mail" value={a.email} />
          <Row icon={Instagram} label="Instagram" value={a.instagram && `@${a.instagram.replace(/^@/, "")}`} />
          <Row icon={Trophy} label="Nível" value={a.level} />
          <Row icon={MoveHorizontal} label="Lado da quadra" value={a.court_side} />
          <Row icon={Clock} label="Joga futevôlei há" value={a.play_time} />
          <Row icon={Home} label="CT" value={a.ct_name} />
          <Row icon={Dumbbell} label="Está treinando?" value={a.training === true ? "Sim" : a.training === false ? "Não" : ""} />
          {a.bio && <p className="py-3 text-sm text-[#cbd5e1]">{a.bio}</p>}
        </div>
      )}
      {tab === "pontos" && (a.points_history.length === 0 ? <Empty icon={Trophy} title="Sem pontos" /> : (
        <div className="space-y-2">
          {a.points_history.map((p) => (
            <div key={p.id} className="card-x flex items-center justify-between p-3">
              <div><div className="text-sm font-semibold">{p.category_label}</div><div className="flex items-center gap-1 text-xs text-[#64748B]"><CalendarDays className="h-3 w-3" />{new Date(p.created_at).toLocaleDateString("pt-BR")} · {p.level}{p.note && ` · ${p.note}`}</div></div>
              <div className="font-display text-2xl font-bold text-[#E4F222]">+{p.points}</div>
            </div>
          ))}
        </div>
      ))}
      {tab === "comentarios" && <Comments uid={a.user_id} />}
      <AthleteForm open={edit} onOpenChange={setEdit} athlete={a} onSaved={() => { load(); if (me) refresh(); }} />
    </div>
  );
}
